import { Box, Button, Heading, Text, useToast } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Layout, Logo } from 'components/atoms'
import { InputBuild } from 'components/molecules'
import { regexForm } from 'components/utils/regexForm'
import { useAuthContext } from 'contexts/auth'
import { AuthDomain } from 'domains/AuthDomain'
import jwt_decode from 'jwt-decode'
import { AuthTokens } from 'models'
import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'
import PasswordListError from '../components/PasswordListError'
import { InputFormChangePassword } from './form'
import { FormResetElementsValues } from './interface'
import { InputFormChangePasswordElements } from './types'

const ChangePassword: React.FC = () => {
    const toast = useToast()
    const { change } = useAuthContext()
    const navigate = useNavigate()
    const schema = yup
        .object({
            nomeUsuario: yup.string().required(),
            senhaAntiga: yup
                .string()
                .min(8, 'Senha deve ter pelo menos 8 caracteres')
                .max(30, 'Senha deve ter no máximo 30 caracteres')
                .required(),
            senhaNova: yup
                .string()
                .min(8, 'Senha deve ter pelo menos 8 caracteres')
                .max(30, 'Senha deve ter no máximo 30 caracteres')
                .matches(regexForm.senha, 'Senha inválida')
                .required(),
            senhaNovaConfirmacao: yup
                .string()
                .min(8, 'Senha deve ter pelo menos 8 caracteres')
                .max(30, 'Senha deve ter no máximo 30 caracteres')
                .matches(regexForm.senha, 'Senha inválida')
                .oneOf([yup.ref('senhaNova'), null], 'Senhas não coincidem.')
                .required()
        })
        .required()

    const {
        handleSubmit,
        register,
        setValue,
        watch,
        reset,
        formState: { errors, isSubmitting }
    } = useForm<FormResetElementsValues>({
        resolver: yupResolver(schema)
    })

    const all = watch()
    const { currentUser } = useAuthContext()

    const decodedIdToken =
        currentUser && (jwt_decode(currentUser.idToken) as AuthTokens)

    useEffect(() => {
        if (decodedIdToken) {
            setValue('nomeUsuario', decodedIdToken?.['cognito:username'])
        }
    }, [])

    const onSubmit = async (values: {
        senhaAntiga: string
        senhaNova: string
        nomeUsuario: string
    }) => {
        try {
            const accessToken = sessionStorage.getItem('accessToken')!
            await change({
                accessToken,
                senhaAntiga: values.senhaAntiga,
                senhaNova: values.senhaNova
            })

            toast({
                title: 'Alterar senha',
                description: 'Sua senha foi alterada com sucesso.',
                status: 'success'
            })

            const authDomain = new AuthDomain()
            authDomain.signOutUser()
        } catch (error: any) {
            const response = error.response
            const errorMessage =
                response?.data?.messsage ||
                'Houve um problema ao alterar sua senha. Por favor, tente novamente.'

            toast({
                title: 'Alterar senha',
                description: errorMessage,
                status: 'error'
            })
        }
    }

    return (
        <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            color="blackAlpha.900"
            bgColor="backdrop"
            w="100%"
        >
            <Box display="grid" w="100%" maxW="4xl" gap={8}>
                <Box>
                    <Logo maxW={187} />
                </Box>
                <Box
                    bg="white"
                    borderRadius="lg"
                    px={{ base: 4, md: 6 }}
                    py={{ base: 4, md: 12 }}
                >
                    <Heading size="md" textAlign="center" mb="2">
                        Alterar Senha
                    </Heading>
                    <Text textAlign="center">
                        Preencha os campos a seguir para alterar a senha
                    </Text>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Layout type="container" full px={6} mt={8}>
                            <Layout type="row" gutter={{ y: 1.5 }}>
                                <>
                                    {InputFormChangePassword.map(
                                        ({ layout, input }, key) => (
                                            <Layout {...layout} key={key}>
                                                <InputBuild
                                                    {...input}
                                                    err={
                                                        errors[
                                                            input.id as InputFormChangePasswordElements
                                                        ]
                                                    }
                                                    {...register(
                                                        input.id as InputFormChangePasswordElements
                                                    )}
                                                    value={
                                                        watch(
                                                            input.id as InputFormChangePasswordElements
                                                        ) as string
                                                    }
                                                    isDisable={
                                                        input.id ===
                                                        'nomeUsuario'
                                                    }
                                                ></InputBuild>
                                            </Layout>
                                        )
                                    )}
                                    <Layout
                                        type="col"
                                        layout={{ base: 12 }}
                                        marginBottom="24px"
                                    >
                                        <PasswordListError
                                            password={all.senhaNova}
                                        />
                                    </Layout>

                                    <Layout
                                        type="col"
                                        layout={{ base: 12, md: 12 }}
                                        display="flex"
                                        justifyContent="flex-end"
                                        alignItems="center"
                                        gap="16px"
                                    >
                                        <Button
                                            size="md"
                                            w={{ md: '200px', sm: 'full' }}
                                            colorScheme="primary"
                                            data-testid="btn-submit-cancel"
                                            variant="outline"
                                            onClick={reset as any}
                                        >
                                            Limpar campos
                                        </Button>

                                        <Button
                                            size="md"
                                            w={{ md: '200px', sm: 'full' }}
                                            colorScheme="primary"
                                            data-testid="btn-submit-finish"
                                            type="submit"
                                            isLoading={isSubmitting}
                                        >
                                            Alterar
                                        </Button>
                                    </Layout>
                                </>
                            </Layout>
                        </Layout>
                    </form>
                </Box>
            </Box>
        </Box>
    )
}

export default ChangePassword
